import React, { useState } from 'react';
import { Plus, Key, Shield, Clock, Trash2, RefreshCw, Eye, Pencil, Ban } from 'lucide-react';
import { PermissionGate } from '../components/common/PermissionGate';
import {
  Button,
  Badge,
  Card,
  PageHeader,
  Alert,
} from '@license-management/ui';
import { ApiKeyModal } from '../components/apikeys/ApiKeyModal';
import { ApiKeyDetailsModal } from '../components/apikeys/ApiKeyDetailsModal';
import { useApiKeys, useDeletedApiKeys, ApiKey } from '../api/hooks/apikeys/useApiKeyQuery';
import { useRevokeApiKey, useReactivateApiKey, useSoftDeleteApiKey } from '../api/hooks/apikeys/useApiKeyMutation';
import ActionMenu, { ActionMenuSection } from '../components/common/ActionMenu';

export const ApiKeyManagement: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const { data, isLoading, error } = useApiKeys();
  const { data: trashData } = useDeletedApiKeys({ page: 1, pageSize: 1 });
  const trashCount = trashData?.totalCount || 0;
  const revokeApiKey = useRevokeApiKey();
  const reactivateApiKey = useReactivateApiKey();
  const softDeleteApiKey = useSoftDeleteApiKey();

  const handleViewDetails = (apiKey: ApiKey) => {
    setSelectedKey(apiKey);
    setIsDetailsModalOpen(true);
    setIsEditMode(false);
  };

  const handleEdit = (apiKey: ApiKey) => {
    setSelectedKey(apiKey);
    setIsDetailsModalOpen(true);
    setIsEditMode(true);
  };

  const handleRevoke = async (apiKey: ApiKey) => {
    if (confirm(`Are you sure you want to revoke the API key "${apiKey.name}"? The key will no longer work for authentication.`)) {
      try {
        await revokeApiKey.mutateAsync(apiKey.id);
      } catch (error) {
        console.error('Failed to revoke API key:', error);
      }
    }
  };

  const handleReactivate = async (apiKey: ApiKey) => {
    if (confirm(`Are you sure you want to reactivate the API key "${apiKey.name}"?`)) {
      try {
        await reactivateApiKey.mutateAsync(apiKey.id);
      } catch (error) {
        console.error('Failed to reactivate API key:', error);
      }
    }
  };

  const handleDelete = async (apiKey: ApiKey) => {
    if (confirm(`Are you sure you want to delete the API key "${apiKey.name}"? It will be moved to the recycle bin and permanently deleted after 30 days.`)) {
      try {
        await softDeleteApiKey.mutateAsync({ keyId: apiKey.id });
      } catch (error) {
        console.error('Failed to delete API key:', error);
      }
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString();
  };

  const getStatusVariant = (apiKey: ApiKey): 'error' | 'warning' | 'success' => {
    if (!apiKey.isActive) return 'error';
    if (apiKey.expiresAt && new Date(apiKey.expiresAt) < new Date()) {
      return 'warning';
    }
    return 'success';
  };

  const getStatusText = (apiKey: ApiKey) => {
    if (!apiKey.isActive) return 'Revoked';
    if (apiKey.expiresAt && new Date(apiKey.expiresAt) < new Date()) {
      return 'Expired';
    }
    return 'Active';
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="API Key Management"
        description="Manage API keys for external integrations"
        actions={
          <div className="flex items-center gap-3">
            <PermissionGate resource="api_keys" action="read">
              <Button
                variant="secondary"
                onClick={() => window.location.href = '/api-keys/trash'}
                leftIcon={<Trash2 className="h-4 w-4" />}
              >
                Recycle Bin
                {trashCount > 0 && (
                  <Badge variant="error" size="sm" className="ml-2">
                    {trashCount}
                  </Badge>
                )}
              </Button>
            </PermissionGate>
            <PermissionGate resource="api_keys" action="create">
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Create API Key
              </Button>
            </PermissionGate>
          </div>
        }
      />

      <Alert variant="info">
        <div className="flex items-start">
          <Shield className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-semibold mb-1">API Key Security</p>
            <p>API keys are used for external integrations such as CI/CD pipelines, scripts, and automation tools.</p>
            <p className="mt-1">Keys can be regenerated if needed - the new key will be shown once after regeneration.</p>
            <p className="mt-1">All keys support IP whitelisting and rate limiting for enhanced security.</p>
          </div>
        </div>
      </Alert>

      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-center text-zinc-500">Loading API keys...</div>
        ) : error ? (
          <div className="p-6 text-center text-red-600">
            Error loading API keys: {error.message}
          </div>
        ) : !data?.apiKeys?.length ? (
          <div className="p-8 text-center">
            <Key size={48} className="mx-auto mb-4 text-zinc-400" />
            <p className="text-zinc-900 font-medium">No API keys found</p>
            <p className="text-sm mt-2 mb-4 text-zinc-500">Create your first API key to get started</p>
            <PermissionGate resource="api_keys" action="create">
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Create API Key
              </Button>
            </PermissionGate>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-zinc-200">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    API Key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-zinc-200">
                {data.apiKeys.map((apiKey) => (
                  <tr key={apiKey.id} className="hover:bg-zinc-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Key className="w-5 h-5 text-zinc-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-zinc-900">
                            {apiKey.name}
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <code className="text-xs text-zinc-500">{apiKey.keyPrefix}...</code>
                            {apiKey.ipWhitelist && apiKey.ipWhitelist.length > 0 && (
                              <Badge variant="warning" size="sm">IP restricted</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={getStatusVariant(apiKey)}>
                        {getStatusText(apiKey)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-500">
                      {formatDate(apiKey.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-500">
                      <div className="space-y-1">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1 text-zinc-400" />
                          {apiKey.lastUsedAt ? formatDate(apiKey.lastUsedAt) : 'Never used'}
                        </div>
                        <div className="text-xs">
                          {apiKey.totalRequests.toLocaleString()} requests • {apiKey.rateLimit || 1000}/hr limit
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(apiKey)}
                          leftIcon={<Eye className="h-4 w-4" />}
                        >
                          View
                        </Button>
                        <PermissionGate resource="api_keys" action="delete">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(apiKey)}
                          >
                            Delete
                          </Button>
                        </PermissionGate>
                        <ActionMenu
                          sections={[
                            {
                              items: [
                                {
                                  label: 'Edit',
                                  icon: <Pencil className="h-4 w-4" />,
                                  onClick: () => handleEdit(apiKey),
                                  permission: { resource: 'api_keys', action: 'update' },
                                },
                              ],
                            },
                            {
                              title: 'Status',
                              items: apiKey.isActive
                                ? [
                                    {
                                      label: 'Revoke',
                                      icon: <Ban className="h-4 w-4" />,
                                      onClick: () => handleRevoke(apiKey),
                                      permission: { resource: 'api_keys', action: 'delete' },
                                    },
                                  ]
                                : [
                                    {
                                      label: 'Reactivate',
                                      icon: <RefreshCw className="h-4 w-4" />,
                                      onClick: () => handleReactivate(apiKey),
                                      permission: { resource: 'api_keys', action: 'update' },
                                    },
                                  ],
                            },
                          ] as ActionMenuSection[]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ApiKeyModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />

      <ApiKeyDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedKey(null);
          setIsEditMode(false);
        }}
        apiKey={selectedKey}
        initialEditMode={isEditMode}
      />
    </div>
  );
};